/* eslint-disable react/destructuring-assignment */
import { DeleteOutlined, EditOutlined, HistoryOutlined, MailOutlined } from '@ant-design/icons'
import { Button, Divider, Popconfirm, Space, Table, Tag } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { parseDate, parseDateTime } from '../../../../../../config/dayjsConfig'
import { userCompaniesSelector } from '../../../../../../redux/session/currentUser/selectors'
import {
  DeliverySubscription,
  DeliverySubscriptionType,
  ManualDelivery
} from '../../../../../../types/deliverySubscription/DeliverySubscription'
import { useTableProps } from '../../../../../../utils/hooks'
import LoadingWrapper from '../../../../../../components/Misc/LoadingWrapper'
import { renderDeliverySubscriptionInitiator } from '../utils'
import Deliveries from './Deliveries'
import { useColumnSearchProps } from '../../../../../../components/Table/useColumSearchProps'
import {
  getTitleByLanguage,
  TranslatableTitle
} from '../../../../../reporting/custom/components/customReportTable/utils'
import { useDeliverySubscriptions } from '../../../../../../features/deliverySubscriptions/queries/useDeliverySubscriptions'
import { useDeleteDeliverySubscription } from '../../../../../../features/deliverySubscriptions/queries/useDeleteDeliverySubscription'
import { useSendDeliverySubscription } from '../../../../../../features/deliverySubscriptions/queries/useSendDeliverySubscription'

type ColumnKey =
  | 'company'
  | 'paramCompany'
  | 'report'
  | 'printVersion'
  | 'user'
  | 'initiator'
  | 'periodLock'
  | 'lastDeliveryAt'
  | 'actions'

const TYPE_COLUMNS: Record<DeliverySubscriptionType, ColumnKey[]> = {
  [DeliverySubscriptionType.ACCOUNTOR_SCORECARD]: [
    'company',
    'user',
    'initiator',
    'periodLock',
    'lastDeliveryAt',
    'actions'
  ],
  [DeliverySubscriptionType.KIINTEISTOMAAILMA_SCORECARD]: [
    'paramCompany',
    'user',
    'initiator',
    'periodLock',
    'lastDeliveryAt',
    'actions'
  ],
  [DeliverySubscriptionType.COMPANY_DASHBOARD]: [
    'company',
    'report',
    'user',
    'initiator',
    'periodLock',
    'lastDeliveryAt',
    'actions'
  ],
  [DeliverySubscriptionType.CUSTOM_REPORT]: [
    'company',
    'report',
    'printVersion',
    'user',
    'initiator',
    'periodLock',
    'lastDeliveryAt',
    'actions'
  ],
  [DeliverySubscriptionType.ALARM]: ['company', 'user', 'lastDeliveryAt', 'actions']
}

interface DeliverySubscriptionTableProps {
  subscriptionType: DeliverySubscriptionType
  toggleDeliverySubscription: Function
  type?: string
}

const DeliverySubscriptionTable: React.FC<DeliverySubscriptionTableProps> = ({
  subscriptionType,
  toggleDeliverySubscription,
  type
}) => {
  const { t } = useTranslation()
  const companies = useSelector(userCompaniesSelector) || []
  const [deliverySubscription, setDeliverySubscription] = useState<DeliverySubscription>()
  const [deliveryModalVisibility, setDeliveryModalVisibility] = useState(false)

  const { data: allSubscriptions = [], isLoading } = useDeliverySubscriptions()
  const { mutate: deleteSubscription, isPending: deleteLoading } = useDeleteDeliverySubscription()
  const { mutate: sendSubscription, isPending: sendLoading, variables: sendingId } = useSendDeliverySubscription()

  const contentEl = useRef(null)
  const tableProps = useTableProps(contentEl)

  const toggleDeliveries = (d: DeliverySubscription) => {
    setDeliverySubscription(d)
    setDeliveryModalVisibility(true)
  }

  let deliverySubscriptions = allSubscriptions.filter(d => d.type === subscriptionType)

  if (
    (subscriptionType === DeliverySubscriptionType.ACCOUNTOR_SCORECARD ||
      subscriptionType === DeliverySubscriptionType.KIINTEISTOMAAILMA_SCORECARD) &&
    deliverySubscriptions
  ) {
    if (type === 'auto') {
      deliverySubscriptions = deliverySubscriptions.filter(d => !(d.initiator as ManualDelivery).manual)
    }
    if (type === 'manual') {
      deliverySubscriptions = deliverySubscriptions.filter(d => (d.initiator as ManualDelivery).manual)
    }
  }

  const userColumnRender = (_: any, d: DeliverySubscription) => d?.recipients?.join(', ') || d.user?.displayName || ''

  const columnMap: Record<ColumnKey, ColumnProps<DeliverySubscription>> = {
    company: {
      title: t('subscriptionsPage:company'),
      key: 'company',
      dataIndex: ['company', 'name'],
      ellipsis: true,
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => a?.company?.name.localeCompare(b?.company?.name || '') || 0,
      ...useColumnSearchProps(['company', 'name'])
    },
    paramCompany: {
      title: t('subscriptionsPage:company'),
      key: 'paramCompany',
      ellipsis: true,
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => {
        const getId = (d: DeliverySubscription) =>
          d.params?.args?.find(arg => arg.type === 'company')?.values?.[0]?.id as string | undefined
        const aName = companies.find(c => c.id === getId(a))?.name || getId(a) || ''
        const bName = companies.find(c => c.id === getId(b))?.name || getId(b) || ''
        return aName.localeCompare(bName)
      },
      render: (_: any, record: DeliverySubscription) => {
        const companyId = record.params?.args?.find(arg => arg.type === 'company')?.values?.[0]?.id as
          | string
          | undefined
        return companies.find(c => c.id === companyId)?.name || companyId || ''
      }
    },
    report: {
      title: t('global:report'),
      key: 'report',
      ellipsis: true,
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => {
        const aTitle = getTitleByLanguage(a as TranslatableTitle)
        const bTitle = getTitleByLanguage(b as TranslatableTitle)
        return aTitle?.localeCompare(bTitle || '') || 0
      },
      render: (_: any, record: DeliverySubscription) => getTitleByLanguage(record as TranslatableTitle)
    },
    printVersion: {
      title: t('subscriptionsPage:print-version'),
      key: 'printVersion',
      width: 90,
      align: 'center',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => (a.params?.printVersion ?? 'v1').localeCompare(b.params?.printVersion ?? 'v1'),
      render: (_value: unknown, record: DeliverySubscription) => {
        const version = record.params?.printVersion ?? 'v1'
        return <Tag color={version === 'v2' ? 'green' : 'orange'}>{version}</Tag>
      }
    },
    user: {
      title: t('subscriptionsPage:user'),
      key: 'recipient',
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) => userColumnRender(null, a).localeCompare(userColumnRender(null, b)),
      ...useColumnSearchProps('', userColumnRender)
    },
    initiator: {
      title: t('subscriptionsPage:initiator'),
      key: 'initiator',
      sortDirections: ['descend', 'ascend'],
      ellipsis: true,
      sorter: (a: any, b: any) =>
        (a?.initiator?.monthly?.day || a?.initiator?.weekly?.dow || 0) -
          (b?.initiator?.monthly?.day || b?.initiator?.weekly?.dow || 0) || 0,
      render: renderDeliverySubscriptionInitiator
    },
    periodLock: {
      title: t('subscriptionsPage:latestPeriodLock'),
      key: 'periodLock',
      ellipsis: true,
      sortDirections: ['descend', 'ascend'],
      sorter: (a, b) =>
        (parseDate(a?.latestPeriodLock || '')?.valueOf() || 0) - (parseDate(b?.latestPeriodLock || '')?.valueOf() || 0),
      render: (ds: DeliverySubscription) => ds?.latestPeriodLock && parseDate(ds.latestPeriodLock)?.format('MM/YYYY')
    },
    lastDeliveryAt: {
      title: t('subscriptionsPage:lastDeliveryAt'),
      key: 'lastDeliveryAt',
      ellipsis: true,
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'descend',
      sorter: (a, b) =>
        (parseDate(a?.lastDeliveryAt || '')?.valueOf() || 0) - (parseDate(b?.lastDeliveryAt || '')?.valueOf() || 0),
      render: (ds: DeliverySubscription) =>
        ds.lastDeliveryAt && parseDateTime(ds.lastDeliveryAt)?.format('DD.MM.YYYY HH:mm:ss')
    },
    actions: {
      title: t('global:actions'),
      key: 'action',
      width: 180,
      align: 'right',
      render: (ds: DeliverySubscription) => (
        <Space size={0} split={<Divider type="vertical" style={{ margin: 4 }} />}>
          <Button size="small" type="text" icon={<HistoryOutlined />} onClick={() => toggleDeliveries(ds)} />
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => toggleDeliverySubscription(ds)} />
          <Button
            size="small"
            type="text"
            icon={<MailOutlined />}
            loading={sendLoading && sendingId === ds.id}
            onClick={() => sendSubscription(ds.id)}
          />
          <Popconfirm
            placement="bottomRight"
            title={t('global:delete-confirm')}
            onConfirm={() => deleteSubscription(ds.id)}
            okText={t('global:yes')}
            cancelText={t('global:no')}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={deleteLoading} />
          </Popconfirm>
        </Space>
      )
    }
  }

  const columns = TYPE_COLUMNS[subscriptionType].map(key => columnMap[key])

  return (
    <LoadingWrapper loading={false}>
      <Deliveries
        deliverySubscription={deliverySubscription}
        setDeliverySubscription={setDeliverySubscription}
        setDeliveryModalVisibility={setDeliveryModalVisibility}
        visible={deliveryModalVisibility}
      />
      <div ref={contentEl}>
        <Table
          {...tableProps}
          loading={isLoading}
          dataSource={deliverySubscriptions}
          columns={columns}
          rowKey={record => record.id.toString()}
          pagination={false}
        />
      </div>
    </LoadingWrapper>
  )
}

export default DeliverySubscriptionTable
